import { Fragment, useMemo, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableExpandedRow,
  TableExpandHeader,
  TableExpandRow,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent
} from '@carbon/react';

import { useNotifications } from 'core/notifications';
import { useIsReadonly } from 'core/readonly';
import EmployeeGroupModal from 'features/inputs/components/EmployeeGroupModal';
import ProjectFormActions from 'features/inputs/components/ProjectFormActions';
import { useTrackProjectFormTabDirtyStatus } from 'features/inputs/components/ProjectFormNavigation';
import useMutateUpdateProjectEmployeePopulation from 'shared/api/projects/useMutateUpdateProjectEmployeePopulation';
import ActionButton from 'shared/components/ActionButton';
import TableExpandText from 'shared/components/data-table/TableExpandText';
import TableToolbarCell from 'shared/components/data-table/TableToolbarCell';
import ModifiedValue from 'shared/components/ModifiedValue';
import { formatNumeric } from 'utils/format';
import useForm from 'utils/hooks/useForm';
import useUpdateEffect from 'utils/hooks/useUpdateEffect';

const GroupsDataTable = ({ groups, onAddGroup, onEditGroup, onRemoveGroup }) => {
  const isReadonly = useIsReadonly();

  const headers = [
    { key: 'businessUnit', header: 'Group' },
    { key: 'subGroup', header: 'Sub-group' },
    { key: 'headcount', header: 'Headcount' },
    { key: 'bandA', header: 'Band A' },
    { key: 'bandAa', header: 'Band AA' },
    { key: 'attendanceRate', header: 'Attendance Rate' }
  ];

  const tableRows = useMemo(
    () =>
      groups.map((x) => ({
        id: x.id.toString(),
        businessUnit: x.businessUnit.name,
        subGroup: x.subGroup.name,
        headcount: formatNumeric(x.headcount),
        bandA: x.bandA,
        bandAa: x.bandAa,
        attendanceRate: `${Math.round((x.attendanceRate ?? x.subGroup.attendanceRate) * 100)}%`,
        isCustomAttendanceRate: x.attendanceRate != null && x.attendanceRate !== x.subGroup.attendanceRate,
        notes: x.notes || ''
      })),
    [groups]
  );

  return (
    <DataTable
      headers={headers}
      rows={tableRows ?? []}
      size="sm"
      render={({ rows, getTableContainerProps, getToolbarProps, getTableProps, getHeaderProps, getRowProps }) => (
        <TableContainer
          title="Group Population Summary"
          description="Please provide information regarding business groups, subgroups, bands, and attendance rates. You can add new groups and provide additional notes on customizations. Expand the row to view notes."
          {...getTableContainerProps()}
        >
          <TableToolbar {...getToolbarProps()} size="sm">
            {!isReadonly && (
              <TableToolbarContent>
                <ActionButton onClick={onAddGroup}>Add Group</ActionButton>
              </TableToolbarContent>
            )}
          </TableToolbar>
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                <TableExpandHeader id="expand" />
                {headers.map((header) => (
                  <TableHeader {...getHeaderProps({ header })} key={header.key}>
                    {header.header}
                  </TableHeader>
                ))}
                {!isReadonly && <TableToolbarCell.Header />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const notes = groups[index]?.notes;
                const isCustomAttendanceRate = tableRows[index]?.isCustomAttendanceRate ?? false;

                const cells = row.cells.slice(0, row.cells.length - 1);
                const attendanceRateCell = row.cells[row.cells.length - 1];

                const attendanceRateCellNode = (
                  <TableCell key={attendanceRateCell.key}>
                    <ModifiedValue isModified={isCustomAttendanceRate}>{attendanceRateCell.value}</ModifiedValue>
                  </TableCell>
                );

                const actionsCellNode = !isReadonly && (
                  <TableToolbarCell>
                    <TableToolbarCell.MenuItem itemText="Edit" onClick={() => onEditGroup(index)} />
                    <TableToolbarCell.MenuItem isDelete itemText="Delete" onClick={() => onRemoveGroup(index)} />
                  </TableToolbarCell>
                );

                return (
                  <Fragment key={row.id}>
                    {notes ? (
                      <>
                        <TableExpandRow expandHeader="expand" {...getRowProps({ row })}>
                          {cells.map((cell) => (
                            <TableCell key={cell.id}>{cell.value}</TableCell>
                          ))}
                          {attendanceRateCellNode}
                          {actionsCellNode}
                        </TableExpandRow>
                        <TableExpandedRow colSpan={headers.length + 2}>
                          <TableExpandText>{notes}</TableExpandText>
                        </TableExpandedRow>
                      </>
                    ) : (
                      <TableRow {...getRowProps({ row })} key={row.id}>
                        <TableCell key="empty-expand" />
                        {cells.map((cell) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                        {attendanceRateCellNode}
                        {actionsCellNode}
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    />
  );
};

const EmployeePopulationForm = ({ values, onSaveChanges, projectId }) => {
  const { showSuccessNotification, showErrorNotification } = useNotifications();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      employeePopulation: values
    }
  });
  const {
    fields: groups,
    append,
    update,
    remove
  } = useFieldArray({
    control,
    name: 'employeePopulation'
  });

  useTrackProjectFormTabDirtyStatus(control);

  useUpdateEffect(() => {
    if (values) {
      reset({
        employeePopulation: values
      });
    }
  }, [values]);

  const updateProjectEmployeePopulationMutation = useMutateUpdateProjectEmployeePopulation({
    onError: () => showErrorNotification({ description: 'Failed to update population' }),
    onSuccess: (project) => {
      onSaveChanges(project);
      showSuccessNotification({ description: 'Population updated successfully' });
    }
  });

  const [employeeGroupEditIndex, setEmployeeGroupEditIndex] = useState(null);
  const [isEmployeeGroupModalOpen, setIsEmployeeGroupModalOpen] = useState(false);

  const handleFormSubmit = ({ employeePopulation }) => {
    updateProjectEmployeePopulationMutation.mutate({
      id: projectId,
      employeePopulation: employeePopulation.map((x) => ({
        headcount: x.headcount,
        bandA: x.bandA,
        bandAa: x.bandAa,
        attendanceRate: x.attendanceRate,
        notes: x.notes,
        businessUnitId: x.businessUnit.id,
        subGroupId: x.subGroup.id
      }))
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <GroupsDataTable
          groups={groups}
          onAddGroup={() => setIsEmployeeGroupModalOpen(true)}
          onEditGroup={(index) => {
            setIsEmployeeGroupModalOpen(true);
            setEmployeeGroupEditIndex(index);
          }}
          onRemoveGroup={remove}
        />
        <ProjectFormActions projectId={projectId} />
      </form>
      <EmployeeGroupModal
        open={isEmployeeGroupModalOpen}
        editGroup={employeeGroupEditIndex === null ? null : groups[employeeGroupEditIndex]}
        onClose={() => {
          setIsEmployeeGroupModalOpen(false);
          setEmployeeGroupEditIndex(null);
        }}
        onSubmit={(value) => (employeeGroupEditIndex === null ? append(value) : update(employeeGroupEditIndex, value))}
      />
    </>
  );
};

export default EmployeePopulationForm;
